import { useState } from 'react';
import { ChevronUpDownIcon, CheckIcon } from '@heroicons/react/20/solid';
import { ClipboardDocumentIcon } from '@heroicons/react/24/outline';
import { generateInstagramReel } from '../../services/openai';
import { useUsage } from '../../contexts/UsageContext';

const toneOptions = ['energetic', 'entertaining', 'educational', 'trendy', 'casual', 'professional'];
const durationOptions = ['15 seconds', '30 seconds', '60 seconds', '90 seconds'];

function InstagramReelsPrompt() {
  const [formData, setFormData] = useState({
    keyMessage: '',
    goal: '',
    targetAudience: '',
    tone: '',
    duration: '',
    visualIdeas: '',
    soundIdeas: '',
    hashtags: ''
  });
  
  const [generatedContent, setGeneratedContent] = useState('');
  const [isOpenTone, setIsOpenTone] = useState(false);
  const [isOpenDuration, setIsOpenDuration] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const { usageStatus, showAd, checkAndRecordRequest } = useUsage();

  const handleShowAd = () => {
    console.log('handleShowAd clicked');
    showAd();
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    setError('');
  };

  const handleGenerateReel = async () => {
    if (!formData.keyMessage || !formData.goal) {
      setError('Please fill in all required fields');
      return;
    }

    if (!checkAndRecordRequest()) {
      setError('Usage limit reached');
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      const content = await generateInstagramReel(formData);
      setGeneratedContent(content);
    } catch (error) {
      setError('Failed to generate reel script. Please try again.');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(generatedContent);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text:', err);
    }
  };

  return (
    <div className="max-w-3xl mx-auto">
      <div className="bg-white rounded-lg shadow p-6">
        <h1 className="text-2xl font-bold text-gray-800 mb-6">Instagram Reels Script Generator</h1>
        
        <div className="prose prose-lg text-gray-600 mb-8">
          I want to create an engaging Instagram Reel that captures attention and delivers value. Here's some context to guide the content:
        </div>

        {error && (
          <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
            {error}
          </div>
        )}

        <div className="space-y-6">
          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Key Message: <span className="text-red-500">*</span></label>
            <div className="flex items-center">
              <textarea
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none min-h-[100px] resize-y"
                value={formData.keyMessage}
                onChange={(e) => handleInputChange('keyMessage', e.target.value)}
                placeholder="What's the main message or story you want to share in your Reel?"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Goal: <span className="text-red-500">*</span></label>
            <div className="flex items-center">
              <input
                type="text"
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                value={formData.goal}
                onChange={(e) => handleInputChange('goal', e.target.value)}
                placeholder="What do you want to achieve? E.g., educate, entertain, showcase a product"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Target Audience:</label>
            <div className="flex items-center">
              <input
                type="text"
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                value={formData.targetAudience}
                onChange={(e) => handleInputChange('targetAudience', e.target.value)}
                placeholder="Who is this Reel for? E.g., young professionals, fitness enthusiasts"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Duration:</label>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                  value={formData.duration}
                  onChange={(e) => handleInputChange('duration', e.target.value)}
                  onFocus={() => setIsOpenDuration(true)}
                  onBlur={(e) => {
                    setTimeout(() => setIsOpenDuration(false), 200);
                  }}
                  placeholder="How long should the Reel be?"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpenDuration(!isOpenDuration);
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                >
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
              </div>
              {isOpenDuration && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {durationOptions.map((duration) => (
                    <li
                      key={duration}
                      className={`relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-primary hover:text-white ${
                        formData.duration === duration ? 'text-primary' : 'text-gray-900'
                      }`}
                      onClick={() => {
                        handleInputChange('duration', duration);
                        setIsOpenDuration(false);
                      }}
                    >
                      <span className="block truncate">{duration}</span>
                      {formData.duration === duration && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Tone:</label>
            <div className="relative">
              <div className="relative">
                <input
                  type="text"
                  className="w-full px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                  value={formData.tone}
                  onChange={(e) => handleInputChange('tone', e.target.value)}
                  onFocus={() => setIsOpenTone(true)}
                  onBlur={(e) => {
                    setTimeout(() => setIsOpenTone(false), 200);
                  }}
                  placeholder="Choose the tone, e.g., energetic, entertaining, educational"
                />
                <button
                  type="button"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsOpenTone(!isOpenTone);
                  }}
                  className="absolute inset-y-0 right-0 flex items-center pr-2"
                >
                  <ChevronUpDownIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
                </button>
              </div>
              {isOpenTone && (
                <ul className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
                  {toneOptions.map((tone) => (
                    <li
                      key={tone}
                      className={`relative cursor-default select-none py-2 pl-10 pr-4 hover:bg-primary hover:text-white ${
                        formData.tone === tone ? 'text-primary' : 'text-gray-900'
                      }`}
                      onClick={() => {
                        handleInputChange('tone', tone);
                        setIsOpenTone(false);
                      }}
                    >
                      <span className="block truncate">{tone}</span>
                      {formData.tone === tone && (
                        <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                          <CheckIcon className="h-5 w-5" aria-hidden="true" />
                        </span>
                      )}
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Visual Ideas:</label>
            <div className="flex items-center">
              <textarea
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none min-h-[100px] resize-y"
                value={formData.visualIdeas}
                onChange={(e) => handleInputChange('visualIdeas', e.target.value)}
                placeholder="Describe any specific visuals, transitions, or effects you'd like to include"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Sound Ideas:</label>
            <div className="flex items-center">
              <input
                type="text"
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                value={formData.soundIdeas}
                onChange={(e) => handleInputChange('soundIdeas', e.target.value)}
                placeholder="Any specific music, sounds, or voice-over ideas?"
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="block text-sm font-medium text-gray-700">Hashtags:</label>
            <div className="flex items-center">
              <input
                type="text"
                className="flex-1 px-3 py-2 border-b-2 border-gray-300 focus:border-primary focus:outline-none"
                value={formData.hashtags}
                onChange={(e) => handleInputChange('hashtags', e.target.value)}
                placeholder="Add relevant hashtags (e.g., #reels #trending #tutorial)"
              />
            </div>
          </div>

          <div className="flex items-center justify-between mt-4">
            <button
              onClick={handleGenerateReel}
              disabled={isLoading}
              className="w-full px-4 py-2 text-white bg-primary rounded-md hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Generating...
                </>
              ) : (
                'Generate Reel Script'
              )}
            </button>
            {usageStatus.allowed && (
              <span className="ml-4 text-sm text-gray-600">
                Requests remaining before ad: {usageStatus.requestsLeft}
              </span>
            )}
          </div>

          {/* Usage Limit Warning with Ad Option */}
          {!usageStatus.allowed && usageStatus.reason === 'cooldown' && (
            <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
              <div className="flex justify-between items-center">
                <div className="flex-1">
                  <p className="text-yellow-700">
                    Usage limit reached. Please wait {Math.ceil(usageStatus.remainingTime / 1000)} seconds or view an ad to help support this site.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={handleShowAd}
                  className="ml-4 px-4 py-2 bg-yellow-100 text-yellow-700 hover:bg-yellow-200 rounded-md flex items-center whitespace-nowrap"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                  </svg>
                  View Ad to Continue
                </button>
              </div>
            </div>
          )}

          {!usageStatus.allowed && usageStatus.reason === 'limit' && (
            <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
              <p className="text-yellow-800 mb-2">Usage limit reached. Please wait {usageStatus.waitTime} seconds or view an ad to continue.</p>
              <button
                type="button"
                onClick={handleShowAd}
                className="px-4 py-2 bg-primary text-white rounded hover:bg-primary/90"
              >
                View Ad to Continue
              </button>
            </div>
          )}

          {error && (
            <div className="mt-4 p-4 bg-red-50 border-l-4 border-red-400 text-red-700">
              {error}
            </div>
          )}

          {generatedContent && (
            <div className="mt-6">
              <div className="flex justify-between items-center mb-2">
                <label className="block text-sm font-medium text-gray-700">Generated Reel Script:</label>
                <button
                  type="button"
                  onClick={handleCopy}
                  className="flex items-center gap-2 px-3 py-1.5 text-gray-500 hover:text-gray-700 bg-white rounded-md hover:bg-gray-100 transition-colors border border-gray-300"
                  title="Copy to clipboard"
                >
                  <ClipboardDocumentIcon className="h-5 w-5" />
                  <span className="text-sm">{copied ? 'Copied!' : 'Copy'}</span>
                </button>
              </div>
              <div className="w-full h-[300px] p-4 bg-white border border-gray-300 rounded-md overflow-y-auto">
                <pre className="whitespace-pre-wrap font-sans text-gray-800">
                  {generatedContent}
                </pre>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default InstagramReelsPrompt;
